<template>
  <div class="m-cap">
    <ol class="m-cap__steps">
      <li v-for="(s, i) in stepLabels" :key="s" :class="{ 'is-on': step === i, 'is-done': step > i }">{{ $t(s) }}</li>
    </ol>

    <!-- Dublettenwarnung: lieber jetzt melden als spaeter zweimal ins WMS buchen. -->
    <div v-if="duplicates.length && !duplicateAck" class="m-cap__dup">
      <strong>{{ $t("returns.duplicate.title") }}</strong>
      <p>{{ $t("returns.duplicate.warning", { list: duplicateList }) }}</p>
      <p class="m-cap__dup-hint">{{ $t("returns.duplicate.hint") }}</p>
      <div class="m-cap__dup-actions">
        <button class="m-cap__dup-open" @click="openDuplicate(duplicates[0])">{{ $t("returns.duplicate.open") }}</button>
        <button class="m-cap__dup-go" @click="duplicateAck = true">{{ $t("returns.duplicate.continue") }}</button>
      </div>
    </div>

    <!-- Ergebnis der Sofortpruefung. Bleibt bis in den Zuordnungsschritt sichtbar,
         damit ein unlesbares Label nicht untergeht. -->
    <div v-for="c in checks" :key="c.kind" class="m-cap__check" :class="'m-cap__check--' + c.state">
      <i class="material-icons" :class="{ 'm-spin': c.state === 'running' }">{{ checkIcons[c.state] }}</i>
      <div class="m-cap__check-body">
        <p>{{ c.message }}</p>
        <button v-if="c.state === 'failed'" class="m-cap__retake" @click="retake(c)">
          {{ $t(c.kind === "ean" ? "returns.extract.retakeEan" : "returns.extract.retakeLabel") }}
        </button>
      </div>
    </div>

    <!-- Schritt 0: Retoure-Art -->
    <section v-if="step === 0">
      <p class="m-cap__hint">{{ $t("returns.wizard.typeHint") }}</p>
      <button v-for="t in returnTypes" :key="t" class="m-cap__typebtn" @click="chooseType(t)">
        <i class="material-icons">{{ typeIcons[t] }}</i>
        <span>{{ $t("returns.types." + t) }}</span>
      </button>
    </section>

    <!-- Schritt 1: Fotos (nur Platzierung "paket" - Artikel-/Abschluss-Kacheln kommen spaeter, P8.1) -->
    <section v-else-if="step === 1">
      <h3 class="m-heading">{{ $t("returns.photos.packageHeading") }}</h3>
      <div class="m-cap__grid">
        <m-photo-tile
          v-for="pt in photoTypesPaket"
          :key="pt.id"
          :ref="'tile-' + pt.id"
          :label="photoTypeLabel(pt)"
          :required="pt.isRequired"
          :thumb-url="slotState[pt.id] && slotState[pt.id].thumbUrl"
          :not-available="!!(slotState[pt.id] && slotState[pt.id].notAvailable)"
          :state="slotState[pt.id] && slotState[pt.id].state"
          @picked="(file) => onPicked(pt, file)"
          @retry="retrySlot(pt)"
          @not-available="markNotAvailable(pt)"
        />
      </div>
      <button class="m-cap__primary" :disabled="!requiredDone || uploadsBusy" @click="goToAssign">
        {{ uploadsBusy ? $t("returns.photos.uploading") : $t("returns.wizard.next") }}
      </button>
    </section>

    <!-- Schritt 2: Zuordnung -->
    <section v-else-if="step === 2">
      <button class="m-cap__extract" :disabled="!!extracting" @click="runExtract('label')">
        <i class="material-icons">{{ extracting === "label" ? "hourglass_top" : "auto_awesome" }}</i>
        <span>{{ extracting === "label" ? $t("returns.extract.running") : $t("returns.extract.button") }}</span>
      </button>

      <div class="m-cap__search">
        <label>{{ $t("returns.match.reference") }}</label>
        <div class="m-cap__searchrow">
          <input v-model="search.referenceNumber" type="search" @keyup.enter="findCandidates" />
          <button class="m-cap__scanbtn" @click="openScanner('reference')" :aria-label="$t('returns.match.scan')">
            <i class="material-icons">qr_code_scanner</i>
          </button>
        </div>
        <label>{{ $t("returns.match.returnTracking") }}</label>
        <input v-model="search.returnTracking" type="search" inputmode="numeric" @keyup.enter="findCandidates" />
        <label>{{ $t("returns.match.senderName") }} / {{ $t("returns.match.senderZipcode") }}</label>
        <div class="m-cap__searchrow">
          <input v-model="search.senderName" type="text" />
          <input v-model="search.senderZipcode" type="text" inputmode="numeric" class="m-cap__zip" />
        </div>
        <button class="m-cap__primary" :disabled="matching" @click="findCandidates">
          {{ matching ? $t("mobile.loading") : $t("returns.match.search") }}
        </button>
      </div>

      <div v-if="diagnosis.length" class="m-cap__diag" :class="{ 'm-cap__diag--hit': hasExactHit }">
        <strong>{{ $t(hasExactHit ? "returns.match.diagnosisTitleHit" : "returns.match.diagnosisTitle") }}</strong>
        <ul>
          <li v-for="(d, i) in diagnosis" :key="i">
            {{ $t("returns.match.diagnosis." + d.key) }} → {{ $t("returns.match.diagnosis." + d.outcome) }}
            <template v-if="d.hitCount">({{ d.hitCount }})</template>
            <template v-if="d.detail"> – {{ d.detail }}</template>
          </li>
        </ul>
      </div>

      <p v-if="searched && !candidates.length" class="m-hint">{{ $t("returns.match.noCandidates") }}</p>

      <ul class="m-cards">
        <li v-for="c in candidates" :key="candidateKey(c)">
          <button class="m-card m-cap__cand" @click="selectedCandidate = c">
            <div class="m-card__head">
              <span class="m-card__title">{{ c.customerName || c.customerOrderId || ("#" + (c.orderId || c.afterbuyOrderId)) }}</span>
              <!-- Reklamationsbilder zum Vorgang (Trello 21.08.) - click.stop, sonst waehlt der Klick den Kandidaten -->
              <span v-if="c.reklaImageCount > 0 && c.invoiceNumber" class="m-cap__rekla" @click.stop="reklaSheet = c.invoiceNumber">
                <span class="material-icons">image</span>{{ c.reklaImageCount }}
              </span>
              <span class="m-cap__score" :class="scoreClass(c.score)">{{ c.score }} %</span>
            </div>
            <p class="m-card__sub">
              <span v-if="mp(c)" class="m-mp" :style="{ background: mp(c).color }">{{ mp(c).short }}</span>
              {{ c.zipcode }} {{ c.town }}<template v-if="!mp(c) && c.platform"> · {{ c.platform }}</template>
              <template v-if="c.shippedDate"> · {{ c.shippedDate | formatDate }}</template>
            </p>
            <!-- Rechnungs- und Bestellnummer im Vorschlag (Kundenwunsch 18.08., P1.c/2.c) -->
            <p v-if="c.invoiceNumber || c.platformOrderId" class="m-card__sub">
              <template v-if="c.invoiceNumber">{{ $t("returns.table.invoice") }}: {{ c.invoiceNumber }}</template>
              <template v-if="c.invoiceNumber && c.platformOrderId"> · </template>
              <template v-if="c.platformOrderId">{{ $t("returns.table.platformOrder") }}: {{ c.platformOrderId }}</template>
            </p>
            <p v-if="c.skus && c.skus.length" class="m-card__sub">{{ c.skus.map((s) => s.quantity + "× " + s.sku).join(", ") }}</p>
            <p class="m-cap__fields">
              <span v-for="f in c.matchedFields" :key="'m' + f" class="m-cap__field m-cap__field--ok">✓ {{ f }}</span>
              <span v-for="f in c.mismatchedFields" :key="'x' + f" class="m-cap__field m-cap__field--bad">✗ {{ f }}</span>
            </p>
            <p v-if="c.memo" class="m-cap__memo">{{ $t("returns.match.memo") }}: {{ c.memo }}</p>
          </button>
        </li>
      </ul>

      <button class="m-cap__secondary" @click="skipAssign">{{ $t("returns.match.skipAssign") }}</button>
    </section>

    <!-- Schritt 3: Positionen -->
    <section v-else-if="step === 3">
      <div v-if="photoTypesArtikel.length" class="m-cap__grid m-cap__grid--sub">
        <m-photo-tile
          v-for="pt in photoTypesArtikel"
          :key="pt.id"
          :ref="'tile-' + pt.id"
          :label="photoTypeLabel(pt)"
          :required="pt.isRequired"
          :thumb-url="slotState[pt.id] && slotState[pt.id].thumbUrl"
          :not-available="!!(slotState[pt.id] && slotState[pt.id].notAvailable)"
          :state="slotState[pt.id] && slotState[pt.id].state"
          @picked="(file) => onPicked(pt, file)"
          @retry="retrySlot(pt)"
          @not-available="markNotAvailable(pt)"
        />
      </div>
      <p class="m-cap__hint">{{ $t("returns.items.hint") }}</p>
      <!-- EAN-Erkennung laeuft noch (Kundenwunsch 25.08.): Spinner statt leerer Liste,
           die erkannte Position traegt sich danach selbst ein -->
      <p v-if="itemsPrefillPending" class="m-hint m-cap__prefill">
        <md-progress-spinner md-mode="indeterminate" :md-diameter="18" :md-stroke="2"></md-progress-spinner>
        {{ $t("returns.extract.checkingEan") }}
      </p>
      <p v-else-if="!items.length" class="m-hint">{{ $t("returns.items.empty") }}</p>
      <div v-for="(item, idx) in items" :key="idx" class="m-cap__item">
        <div class="m-cap__itemhead">
          <!-- Nur auswaehlbar, nicht tippbar: eine frei eingetippte SKU gibt es im Artikelstamm
               womoeglich gar nicht und faellt erst beim Einbuchen auf -->
          <button class="m-cap__sku m-cap__skubtn" :class="{ 'is-empty': !item.sku }" @click="openSkuPicker(idx)">
            {{ item.sku || $t("returns.items.skuChoose") }}
            <i class="material-icons">arrow_drop_down</i>
          </button>
          <div class="m-cap__qty">
            <button @click="item.quantity = Math.max(1, item.quantity - 1)">−</button>
            <span>{{ item.quantity }}</span>
            <button @click="item.quantity += 1">+</button>
          </div>
          <button class="m-cap__remove" @click="items.splice(idx, 1)"><i class="material-icons">close</i></button>
        </div>
        <div class="m-chips">
          <button class="m-chip" :class="{ 'm-chip--on': item.condition === 'A' }" @click="item.condition = 'A'">
            {{ $t("returns.items.conditionA") }}
          </button>
          <button class="m-chip" :class="{ 'm-chip--on': item.condition === 'B' }" @click="item.condition = 'B'">
            {{ $t("returns.items.conditionB") }}
          </button>
          <button class="m-chip" :class="{ 'm-chip--on': item.condition === 'S' }" @click="item.condition = 'S'">
            {{ $t("returns.items.conditionS") }}
          </button>
          <button class="m-chip" :class="{ 'm-chip--on': item.condition === 'U' }" @click="item.condition = 'U'">
            {{ $t("returns.items.conditionU") }}
          </button>
        </div>
        <!-- Die B-Ware-SKU folgt immer der Konvention aus Products.vue, deshalb nur anzeigen statt eingeben -->
        <p v-if="item.condition === 'B' && item.sku" class="m-cap__bsku">{{ $t("returns.items.bSku") }}: {{ bwareSku(item.sku) }}</p>
        <p v-if="item.condition === 'S' || item.condition === 'U'" class="m-cap__bsku">{{ $t("returns.items.scrapHint") }}</p>
        <!-- Gegenstueck zur automatischen B-Vorbelegung: A-Zustand auf einem B-Ware-Artikel ist fast immer ein Vertipper -->
        <p v-if="item.condition === 'A' && isBwareSku(item.sku)" class="m-cap__warn">{{ $t("returns.items.abMismatch", { sku: item.sku }) }}</p>
      </div>
      <button class="m-cap__secondary" @click="items.push({ sku: '', quantity: 1, condition: 'A' })">
        + {{ $t("returns.items.addItem") }}
      </button>
      <button class="m-cap__primary" :disabled="saving" @click="saveItems">{{ $t("returns.wizard.next") }}</button>
    </section>

    <!-- Schritt 4: Abschluss -->
    <section v-else-if="step === 4">
      <ul class="m-cap__summary">
        <li>
          <i class="material-icons" :class="requiredDone ? 'ok' : 'bad'">{{ requiredDone ? "check_circle" : "error" }}</i>
          {{ requiredDone ? $t("returns.summary.photosOk") : $t("returns.summary.photosMissing") }}
        </li>
        <li>
          <i class="material-icons" :class="assignedLabel ? 'ok' : 'warn'">{{ assignedLabel ? "check_circle" : "help" }}</i>
          {{ assignedLabel || $t("returns.summary.noOrder") }}
        </li>
        <li v-for="(item, i) in items" :key="i">
          <i class="material-icons ok">inventory_2</i>
          {{ item.quantity }}× {{ item.sku }} ({{ conditionLabel(item.condition) }})
        </li>
      </ul>

      <!-- Notiz: auch das Lager kann hier Infos ergaenzen; gespeichert beim Verlassen des Felds -->
      <div class="m-cap__notes">
        <label>{{ $t("returns.detail.notes") }}</label>
        <textarea v-model="notes" rows="2" :placeholder="$t('returns.detail.notesHint')" @blur="saveNotes"></textarea>
      </div>

      <div v-if="photoTypesAbschluss.length" class="m-cap__grid m-cap__grid--sub">
        <m-photo-tile
          v-for="pt in photoTypesAbschluss"
          :key="pt.id"
          :ref="'tile-' + pt.id"
          :label="photoTypeLabel(pt)"
          :required="pt.isRequired"
          :thumb-url="slotState[pt.id] && slotState[pt.id].thumbUrl"
          :not-available="!!(slotState[pt.id] && slotState[pt.id].notAvailable)"
          :state="slotState[pt.id] && slotState[pt.id].state"
          @picked="(file) => onPicked(pt, file)"
          @retry="retrySlot(pt)"
          @not-available="markNotAvailable(pt)"
        />
      </div>

      <!-- Ziellagerplatz nur wenn tatsaechlich gebucht wird - Schrott/Benutzt buchen nicht (P10.2) -->
      <div v-if="canBook && bookingLocations.length && bookableItems.length" class="m-cap__booking">
        <p v-if="bookingWarning" class="m-cap__warn">{{ $t("returns.booking.alreadyWarning") }}</p>
        <label>{{ $t("returns.booking.location") }}</label>
        <select v-model="selectedLocationId" class="m-cap__select">
          <option v-for="l in bookingLocations" :key="l.id" :value="l.id">{{ l.display }}</option>
        </select>
        <!-- Barcode zuerst: der Scan ist eindeutig, das Vision-Lesen nur der Rueckfall
             fuer Regale, an denen noch kein neues Schild haengt. -->
        <button class="m-cap__scanloc" @click="openScanner('location')">
          <i class="material-icons">qr_code_scanner</i>
          <span>{{ $t("returns.location.scan") }}</span>
        </button>
        <button v-if="locationPhotoType" class="m-cap__scanloc" :disabled="!!extracting" @click="$refs.locInput.click()">
          <i class="material-icons">{{ extracting === "location" ? "hourglass_top" : "photo_camera" }}</i>
          <span>{{ extracting === "location" ? $t("returns.location.running") : $t("returns.location.button") }}</span>
        </button>
        <input ref="locInput" type="file" accept="image/*" capture="environment" class="m-cap__hiddenfile" @change="onLocationPicked" />
        <p v-if="locationCheck" class="m-cap__loccheck" :class="'is-' + locationCheck.state">{{ locationCheck.message }}</p>
        <button class="m-cap__primary" :disabled="saving || !selectedLocationId" @click="book">
          {{ $t("returns.booking.book") }}
        </button>
        <p class="m-hint m-hint--small">{{ $t("returns.booking.laterHint") }}</p>
      </div>
      <p v-if="items.length && !bookableItems.length" class="m-hint m-hint--small">{{ $t("returns.items.scrapHint") }}</p>

      <button class="m-cap__secondary" :disabled="saving" @click="finish">{{ $t("returns.summary.finish") }}</button>
    </section>

    <m-sheet v-if="skuPickerIdx !== null" :title="$t('returns.items.skuChoose')" @close="skuPickerIdx = null">
      <input v-model="skuSearch" ref="skuSearch" type="search" class="m-cap__sku" :placeholder="$t('returns.items.skuSearch')" />
      <p v-if="skuLoading" class="m-hint">{{ $t("mobile.loading") }}</p>
      <p v-else-if="!filteredSkus.length" class="m-hint">{{ $t("returns.items.skuNoMatch") }}</p>
      <ul v-else class="m-cards m-cap__skulist">
        <li v-for="p in filteredSkus" :key="p.sku">
          <button class="m-card m-cap__cand" @click="chooseSku(p)">
            <div class="m-card__head">
              <span class="m-card__title">{{ p.sku }}</span>
              <span v-if="isBware(p)" class="m-cap__bwaretag">{{ $t("returns.items.conditionB") }}</span>
            </div>
            <p v-if="p.name" class="m-card__sub">{{ p.name }}</p>
          </button>
        </li>
      </ul>
      <p v-if="skuOverflow" class="m-hint m-hint--small">{{ $t("returns.items.skuMore", { count: skuOverflow }) }}</p>
    </m-sheet>

    <m-barcode-scanner v-if="scannerOpen" @close="scannerOpen = false" @scanned="onScanned" />

    <!-- Reklamationsbilder-Ansicht: eigenes Sheet, X schliesst NUR das Sheet, nie den Wizard -->
    <m-sheet v-if="reklaSheet" :title="$t('orders.images.title') + ' ' + reklaSheet" @close="reklaSheet = null">
      <OrderImageGallery :invoice-number="reklaSheet" :allow-upload="false" />
    </m-sheet>

    <m-sheet v-if="selectedCandidate" :title="$t('returns.match.title')" @close="selectedCandidate = null">
      <p>
        <strong>{{ selectedCandidate.customerName }}</strong
        ><br />
        {{ selectedCandidate.street }}<br />
        {{ selectedCandidate.zipcode }} {{ selectedCandidate.town }}
      </p>
      <p v-if="selectedCandidate.skus && selectedCandidate.skus.length">
        {{ selectedCandidate.skus.map((s) => s.quantity + "× " + s.sku).join(", ") }}
      </p>
      <p v-if="selectedCandidate.memo" class="m-cap__memo">{{ selectedCandidate.memo }}</p>
      <button class="m-cap__primary" :disabled="saving" @click="assign(selectedCandidate)">
        {{ $t("returns.match.confirm") }}
      </button>
    </m-sheet>
  </div>
</template>

<script>
import { findByGtin } from "@/helpers/gtin";
import { marketplaceInfo } from "@/helpers/marketplaces";
import ReturnsService from "@/services/returns.service";
import ShippingDataService from "@/services/shipping-data.service";
import { hasPermission } from "@/mixins/auth";
import TokenService from "@/services/token.service";
import { compressImage } from "@/helpers/imageCompress";
import MPhotoTile from "./components/MPhotoTile.vue";
import MSheet from "./components/MSheet.vue";
import OrderImageGallery from "@/components/Orders/OrderImageGallery.vue";
import MBarcodeScanner from "./components/MBarcodeScanner.vue";

// Erfassungs-Wizard, Draft-first: die Retoure entsteht beim ersten Schritt, jedes Foto
// laedt sofort einzeln hoch (sequenzielle Queue im Service). Abbruch hinterlaesst einen
// fortsetzbaren Entwurf in der Liste.
export default {
  name: "MobileReturnCapture",
  components: { MPhotoTile, MSheet, MBarcodeScanner, OrderImageGallery },
  data() {
    return {
      step: 0,
      stepLabels: [
        "returns.wizard.stepType",
        "returns.wizard.stepPhotos",
        "returns.wizard.stepAssign",
        "returns.wizard.stepItems",
        "returns.wizard.stepDone",
      ],
      returnTypes: ["customer_return", "undeliverable", "amazon_fba", "other"],
      chosenType: null,
      // SKU aus der EAN-/Boxbild-Erkennung - Vorauswahl fuer die Positionen (P10)
      extractedSku: null,
      itemsPrefillPending: false,
      // amazon_fba: Platzhalter-Icon, bis der Kunde das Amazon-Logo liefert (SVG 64px)
      typeIcons: { customer_return: "assignment_return", undeliverable: "wrong_location", amazon_fba: "inventory", other: "help_outline" },
      returnId: null,
      photoTypes: [],
      slotState: {},
      uploadQueue: Promise.resolve(),
      pendingUploads: 0,
      search: { referenceNumber: "", returnTracking: "", senderName: "", senderZipcode: "" },
      // Vorbefuellter Suchwert (z.B. Rechnungsnummer beim Entwurf-Fortsetzen) - der darf beim
      // Suchen nicht als Referenznummer an die Retoure geschrieben werden
      prefillReference: "",
      candidates: [],
      diagnosis: [],
      searched: false,
      matching: false,
      // null | "label" | "ean" - es laeuft immer nur eine Erkennung
      // Mehrere Extraktionen (Label/EAN/Lagerplatz) koennen sich ueberlappen - erst wenn die
      // letzte fertig ist, werden die Buttons wieder frei
      extractingKinds: [],
      // Ergebnis der Sofortpruefung je Foto: { kind, state, message, photoTypeId }
      labelCheck: null,
      eanCheck: null,
      // Ergebnis des Lagerplatzschilds - erscheint im Buchungsblock, nicht im oberen Banner
      locationCheck: null,
      labelExtracted: false,
      duplicates: [],
      duplicateAck: false,
      checkIcons: { running: "sync", ok: "check_circle", failed: "error" },
      selectedCandidate: null,
      reklaSheet: null,
      assignedLabel: null,
      scannerOpen: false,
      // "reference" = Label-Barcode in der Zuordnung, "location" = Lagerplatz-Schild
      scannerZiel: "reference",
      // SKU-Auswahl: Artikelstamm einmal laden (schlanke Variante), danach lokal filtern
      skuPickerIdx: null,
      skuSearch: "",
      skuLoading: false,
      allSkus: [],
      items: [],
      serverItems: [],
      bookingLocations: [],
      bookingWarning: false,
      selectedLocationId: null,
      notes: "",
      saving: false,
    };
  },
  computed: {
    // Schrott (S) wird nie eingebucht
    bookableItems() {
      // Schrott und Benutzt werden nicht gebucht (P10.2)
      return this.items.filter((i) => i.condition !== "S" && i.condition !== "U");
    },
    extracting() {
      return this.extractingKinds.length ? this.extractingKinds[this.extractingKinds.length - 1] : null;
    },
    photoTypesPaket() {
      const paket = this.photoTypes.filter((t) => (t.placement || "paket") === "paket");
      // FBA: nur Artikel-Foto + EAN-Sticker anbieten (Kundenwunsch 25.08.) - die uebrigen
      // Paket-Kacheln (Karton/Paketschein) sind dort ueberfluessig
      if (this.chosenType === "amazon_fba") {
        return paket.filter((t) => t.code === "ean_sticker" || t.code === "return_item");
      }
      return paket;
    },
    photoTypesArtikel() {
      return this.photoTypes.filter((t) => t.placement === "artikel");
    },
    photoTypesAbschluss() {
      return this.photoTypes.filter((t) => t.placement === "abschluss");
    },
    locationPhotoType() {
      return this.photoTypes.find((t) => t.code === "storage_location");
    },
    // Bei 2.877 Artikeln ist Filtern im Browser schnell genug; gerendert wird trotzdem nur
    // ein Ausschnitt, sonst baut das Handy tausende DOM-Knoten auf.
    matchingSkus() {
      const q = this.skuSearch.trim().toLowerCase();
      if (!q) return this.allSkus;
      // GTIN mit drin: ein Barcode-Scan ins Suchfeld trifft den Artikel direkt
      return this.allSkus.filter((p) => (p.sku || "").toLowerCase().includes(q) || (p.name || "").toLowerCase().includes(q) || (p.gtin || "").includes(q));
    },
    filteredSkus() {
      return this.matchingSkus.slice(0, 50);
    },
    skuOverflow() {
      return Math.max(0, this.matchingSkus.length - this.filteredSkus.length);
    },
    requiredDone() {
      // FBA-Retouren: nur Artikel-Foto + EAN-Sticker sind Pflicht (Kundenwunsch 25.08., wie im
      // Backend) - Paketfotos und Versandlabel entfallen komplett.
      // Nur die in Schritt 1 sichtbaren Paket-Kacheln zaehlen - eine Pflicht-Fotoart mit
      // Platzierung artikel/abschluss wuerde den Weiter-Button sonst dauerhaft sperren.
      const required =
        this.chosenType === "amazon_fba"
          ? this.photoTypesPaket.filter((t) => t.code === "ean_sticker" || t.code === "return_item")
          : this.photoTypesPaket.filter((t) => t.isRequired);
      return required.every((t) => {
        const s = this.slotState[t.id];
        return s && (s.notAvailable || (s.photoId && s.state === "done"));
      });
    },
    uploadsBusy() {
      return this.pendingUploads > 0;
    },
    canBook() {
      // Frontend-Gate; der Server prueft das Recht Returns/Book nochmal
      return hasPermission("Write", "Returns", "Book");
    },
    hasExactHit() {
      return this.diagnosis.some((d) => d.outcome === "exact_hit");
    },
    checks() {
      // Nur in Fotos/Zuordnung relevant - danach ist das Label laengst verarbeitet
      if (this.step > 2) return [];
      return [this.labelCheck, this.eanCheck].filter(Boolean);
    },
    duplicateList() {
      return this.duplicates.map((d) => "#" + d.id + (d.bookedOn ? " (" + this.$t("returns.duplicate.booked") + ")" : "")).join(", ");
    },
  },
  async created() {
    // Vorhandenen Entwurf fortsetzen (?id=) oder neu starten
    const draftId = parseInt(this.$route.query.id, 10);
    this.photoTypes = await ReturnsService.photoTypes();
    if (draftId) {
      this.returnId = draftId;
      const detail = await this.restoreDraft();
      // Schutz im Parallelbetrieb: fremde Entwuerfe nicht versehentlich uebernehmen -
      // sonst landen Positionen/Buchung im Eintrag eines Kollegen (Vorfall 19.08.)
      const me = TokenService.getUser();
      if (detail && detail.createdById && me && me.userId && detail.createdById !== me.userId) {
        const frage = this.$t("returns.wizard.foreignDraft", { name: detail.createdBy || "?" });
        if (!window.confirm(frage)) {
          this.$router.replace("/m/retoure");
          return;
        }
      }
      this.step = 1;
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.uploadsBusy && !window.confirm(this.$t("returns.wizard.leaveWarning"))) {
      next(false);
      return;
    }
    next();
  },
  watch: {
    // Scanner im SKU-Picker: eine eindeutige GTIN wird sofort als SKU uebernommen (Update 27.08.)
    skuSearch(value) {
      if (this.skuPickerIdx === null) return;
      const hit = findByGtin(this.allSkus, value);
      if (hit) this.chooseSku(hit);
    },
  },
  methods: {
    mp(c) {
      return marketplaceInfo(c && c.marketplace);
    },
    photoTypeLabel(pt) {
      if (pt.name) return pt.name;
      const key = "returns.photoTypes." + pt.code;
      return this.$te(key) ? this.$t(key) : pt.code;
    },
    notifyError(err, fallback) {
      const reason = (err && err.response && err.response.data && err.response.data.reason) || fallback;
      const key = "returns.errors." + reason;
      this.$notify({
        timeout: 3000,
        message: this.$te(key) ? this.$t(key) : this.$t("returns.errors." + fallback),
        icon: "error",
        horizontalAlign: "right",
        verticalAlign: "top",
        type: "danger",
      });
    },
    notifyOk(key) {
      this.$notify({ timeout: 2000, message: this.$t(key), icon: "check", horizontalAlign: "right", verticalAlign: "top", type: "success" });
    },

    // --- Schritt 0 ---
    async chooseType(type) {
      try {
        if (!this.returnId) {
          const created = await ReturnsService.create(type);
          this.returnId = created.id;
          // Id in die URL: nach einem Reload landet man wieder in GENAU diesem Entwurf
          this.$router.replace({ query: { id: String(created.id) } }).catch(() => {});
        } else {
          await ReturnsService.update(this.returnId, { returnType: type });
        }
        this.chosenType = type;
        this.step = 1;
      } catch (err) {
        this.notifyError(err, "load_failed");
      }
    },

    // --- Schritt 1: Fotos ---
    async restoreDraft() {
      // Kompletten Stand wiederherstellen: Fotos, gespeicherte Suchdaten, Zuordnung, Positionen
      const detail = await ReturnsService.get(this.returnId);
      this.chosenType = detail.returnType || this.chosenType;
      this.extractedSku = detail.productSku || this.extractedSku;

      for (const p of detail.photos || []) {
        const state = { photoId: p.id, state: "done", notAvailable: p.isNotAvailable, thumbUrl: null };
        if (!p.isNotAvailable) {
          state.thumbUrl = await ReturnsService.photoContentUrl(this.returnId, p.id).catch(() => null);
        }
        this.$set(this.slotState, p.photoTypeId, state);
      }

      this.search.referenceNumber = detail.invoiceNumber || detail.referenceNumber || "";
      this.prefillReference = detail.invoiceNumber && !detail.referenceNumber ? detail.invoiceNumber : "";
      this.search.returnTracking = detail.returnTrackingNumber || "";
      this.search.senderName = detail.senderName || "";
      this.search.senderZipcode = detail.senderZipcode || "";
      this.notes = detail.notes || "";

      // Ein fortgesetzter Entwurf hat das Label schon gelesen - nicht noch einmal starten
      this.labelExtracted = !!(detail.referenceNumber || detail.senderName);
      if (detail.returnTrackingNumber) this.checkDuplicates(detail.returnTrackingNumber);

      if (detail.orderId || detail.afterbuyOrderId) {
        this.assignedLabel = detail.customerOrderId || "#" + (detail.orderId || detail.afterbuyOrderId);
      }
      if (detail.items && detail.items.length) {
        this.items = detail.items.map((i) => ({ sku: i.sku, quantity: i.quantity, condition: i.condition }));
      } else if (detail.orderSkus && detail.orderSkus.length) {
        this.items = detail.orderSkus.map((s) => ({ sku: s.sku, quantity: s.quantity, condition: "A" }));
      }

      // Mit vorhandenen Suchschluesseln direkt Kandidaten laden - der Zuordnungs-Schritt
      // soll beim Fortsetzen nicht leer wirken
      if (this.search.referenceNumber || (this.search.senderName && this.search.senderZipcode)) {
        this.findCandidates();
      }
      return detail;
    },
    onPicked(pt, file) {
      const isLabel = pt.code === "shipping_label";
      this.$set(this.slotState, pt.id, {
        ...(this.slotState[pt.id] || {}),
        file,
        notAvailable: false,
        state: "uploading",
        thumbUrl: URL.createObjectURL(file),
      });
      this.enqueueUpload(pt, file, isLabel);
    },
    enqueueUpload(pt, file, isLabel) {
      this.pendingUploads += 1;
      this.uploadQueue = this.uploadQueue.then(async () => {
        try {
          const compressed = await compressImage(file, isLabel ? { maxEdge: 2048, quality: 0.85 } : { maxEdge: 1600, quality: 0.8 });
          const uploaded = await ReturnsService.uploadPhoto(this.returnId, pt.id, compressed, (state) => {
            const s = this.slotState[pt.id];
            if (s) this.$set(this.slotState, pt.id, { ...s, state });
          });
          const s = this.slotState[pt.id];
          this.$set(this.slotState, pt.id, { ...s, photoId: uploaded.id, state: "done" });
          // Direkt nach dem Upload lesen: ein unlesbares Label soll auffallen,
          // solange der Mitarbeiter noch am Paket steht.
          if (isLabel) this.runExtract("label");
          else if (pt.code === "ean_sticker") this.runExtract("ean");
          else if (pt.code === "storage_location") this.runExtract("location");
        } catch (err) {
          this.notifyError(err, "upload_failed");
        } finally {
          this.pendingUploads -= 1;
        }
      });
    },
    /** Schild direkt aus dem Buchungsschritt aufnehmen - der Upload stoesst das Lesen an. */
    onLocationPicked(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (file && this.locationPhotoType) this.onPicked(this.locationPhotoType, file);
    },
    retrySlot(pt) {
      const s = this.slotState[pt.id];
      if (s && s.file) this.enqueueUpload(pt, s.file, pt.code === "shipping_label");
    },
    /** Kamera fuer den betroffenen Slot erneut oeffnen (Label unlesbar). */
    retake(check) {
      const pt = this.photoTypes.find((t) => t.code === (check.kind === "ean" ? "ean_sticker" : "shipping_label"));
      if (!pt) return;
      this.step = 1;
      this.$nextTick(() => {
        const tile = this.$refs["tile-" + pt.id];
        const instance = Array.isArray(tile) ? tile[0] : tile;
        if (instance) instance.open();
      });
    },
    async markNotAvailable(pt) {
      try {
        const marker = await ReturnsService.markNotAvailable(this.returnId, pt.id);
        this.$set(this.slotState, pt.id, { photoId: marker && marker.id, notAvailable: true, state: "done", thumbUrl: null });
      } catch (err) {
        this.notifyError(err, "load_failed");
      }
    },

    // --- Extraktion (laeuft ab Schritt 1, direkt nach dem Upload) ---
    // Liest ein Foto per Vision-Modell und uebernimmt die gelesenen Werte in die
    // Suchfelder. Zugeordnet wird nichts - der Mitarbeiter bestaetigt wie sonst auch.
    goToAssign() {
      // Amazon-FBA-Retouren haben keinen Vorgang - direkt zu den Positionen (P7.1)
      if (this.chosenType === "amazon_fba") {
        this.step = 3;
        this.prefillItems();
        return;
      }
      this.step = 2;
      const hatSuchdaten = this.search.referenceNumber || (this.search.senderName && this.search.senderZipcode);
      const hatLabelFoto = this.photoTypes.some((t) => {
        const s = this.slotState[t.id];
        return t.code === "shipping_label" && s && s.photoId && !s.notAvailable;
      });
      // Regelfall ist die Sofortpruefung aus Schritt 1; das hier faengt nur die Faelle,
      // in denen sie nicht gelaufen ist (fortgesetzter Entwurf ohne gelesene Daten).
      if (!hatSuchdaten && hatLabelFoto && !this.labelExtracted && !this.extracting) this.runExtract("label");
    },
    async runExtract(kind) {
      const istEan = kind === "ean";
      const istOrt = kind === "location";
      const feld = istEan ? "eanCheck" : istOrt ? "locationCheck" : "labelCheck";
      const fehlerPfad = istEan ? "returns.extract.eanErrors." : istOrt ? "returns.location.errors." : "returns.extract.errors.";
      const laeuft = istEan ? "returns.extract.checkingEan" : istOrt ? "returns.location.checking" : "returns.extract.checking";

      this.extractingKinds = this.extractingKinds.concat(kind);
      this[feld] = { kind, state: "running", message: this.$t(laeuft) };

      try {
        const res = await ReturnsService.extract(this.returnId, kind);
        if (!res.success) {
          const key = fehlerPfad + res.error;
          const message = this.$te(key) ? this.$t(key) : this.$t(fehlerPfad + "failed");
          // Unlesbar heisst: neu aufnehmen. Alles andere ist ein Systemproblem,
          // das der Mitarbeiter nicht durch ein besseres Foto loest.
          const nochmal = res.error === "unreadable" || res.error === "unknown_location";
          this[feld] = { kind, state: nochmal ? "failed" : "ok", message: this.ortsFehler(res, message) };
          return;
        }

        this[feld] = istEan ? this.uebernimmEan(res) : istOrt ? this.uebernimmOrt(res) : await this.uebernimmLabel(res);
      } catch (err) {
        // Netzwerk-/Serverfehler duerfen kein gruenes Haekchen zeigen
        this[feld] = { kind, state: "failed", message: this.$t(fehlerPfad + "failed") };
      } finally {
        this.extractingKinds = this.extractingKinds.filter((k) => k !== kind);
      }
    },
    /** Bei "unknown_location" zeigt die Meldung den gelesenen Code - sonst raet der Erfasser. */
    ortsFehler(res, message) {
      const gelesen = res.location && (res.location.location || res.location.locationRaw);
      return res.error === "unknown_location" && gelesen
        ? this.$t("returns.location.errors.unknown_location", { code: gelesen })
        : message;
    },
    uebernimmOrt(res) {
      const l = res.location || {};
      this.waehleLagerplatz(l.storageLocationId, l.location);
      return { kind: "location", state: "ok", message: this.$t("returns.location.read", { code: l.location }) };
    },
    /**
     * Der Platz ist oft kein Retouren-Platz und steht deshalb nicht in der Vorschlagsliste -
     * dann als Option ergaenzen, sonst laesst er sich gar nicht auswaehlen.
     */
    waehleLagerplatz(id, display) {
      if (!this.bookingLocations.some((b) => b.id === id)) {
        this.bookingLocations = [...this.bookingLocations, { id, display }];
      }
      this.selectedLocationId = id;
    },
    /** Gescannter Lagerplatz-Barcode: eindeutig, keine Bestaetigung durch ein Modell noetig. */
    async uebernimmScanOrt(value) {
      this.locationCheck = { kind: "location", state: "running", message: this.$t("returns.location.checkingScan") };
      try {
        const l = await ReturnsService.storageLocation(value);
        if (!l || !l.locationKnown) {
          this.locationCheck = {
            kind: "location",
            state: "failed",
            message: l && l.location
              ? this.$t("returns.location.errors.unknown_location", { code: l.location })
              : this.$t("returns.location.errors.no_location_code", { code: value }),
          };
          return;
        }
        this.waehleLagerplatz(l.storageLocationId, l.location);
        this.locationCheck = { kind: "location", state: "ok", message: this.$t("returns.location.scanned", { code: l.location }) };
      } catch (err) {
        this.locationCheck = { kind: "location", state: "failed", message: this.$t("returns.location.errors.failed") };
      }
    },
    async uebernimmLabel(res) {
      const p = res.proposal || {};
      this.labelExtracted = true;
      if (p.invoiceNumber || p.referenceNumber) {
        this.search.referenceNumber = p.invoiceNumber || p.referenceNumber;
        this.prefillReference = p.invoiceNumber && !p.referenceNumber ? p.invoiceNumber : "";
      }
      if (p.returnTracking) this.search.returnTracking = p.returnTracking;
      if (p.sender && p.sender.name) this.search.senderName = p.sender.name;
      if (p.sender && p.sender.zipcode) this.search.senderZipcode = p.sender.zipcode;

      // Erkannte Label-Daten an der Retoure festhalten (Tracking, Kanal, Absender).
      // Rechnungs- und Referenznummer getrennt speichern - die Suche kombiniert sie nur.
      await ReturnsService.update(this.returnId, {
        referenceNumber: p.referenceNumber || null,
        invoiceNumber: p.invoiceNumber || null,
        returnTrackingNumber: p.returnTracking || null,
        channel: p.channel || null,
        senderName: p.sender && p.sender.name,
        senderStreet: p.sender && p.sender.street,
        senderZipcode: p.sender && p.sender.zipcode,
        senderTown: p.sender && p.sender.town,
        senderCountry: p.sender && p.sender.country,
      }).catch(() => {});

      const parts = [];
      if (p.suggestedReturnType) parts.push(this.$t("returns.types." + p.suggestedReturnType));
      if (this.search.referenceNumber) parts.push(this.search.referenceNumber);
      if (p.returnTracking) parts.push(p.returnTracking);
      if (p.sender && p.sender.name) parts.push(p.sender.name);
      if (p.skuLine) parts.push(p.skuLine);
      let message = this.$t("returns.extract.read", { info: parts.join(" · ") });
      // Die API verwirft einen Absender, der auf die eigene Firmenanschrift zeigt (Empfaengerblock
      // des Labels). Ohne Hinweis wuerde der Erfasser raetseln, warum das Absenderfeld leer bleibt.
      if (p.senderIsOwnAddress) message += " – " + this.$t("returns.extract.ownAddress");

      if (res.match) {
        this.candidates = res.match.candidates || [];
        this.diagnosis = res.match.diagnosis || [];
        this.searched = true;
      }
      if (res.duplicates && res.duplicates.length) {
        this.duplicates = res.duplicates;
        this.duplicateAck = false;
      }
      return { kind: "label", state: "ok", message };
    },
    uebernimmEan(res) {
      const e = res.ean || {};
      if (e.sku && e.skuKnown !== false) this.extractedSku = e.sku;
      const parts = [];
      if (e.sku) parts.push(e.sku);
      if (e.ean) parts.push("EAN " + e.ean);
      if (e.productName) parts.push(e.productName);
      let message = this.$t("returns.extract.readEan", { info: parts.join(" · ") });
      // Eine unbekannte SKU wird serverseitig verworfen - das gehoert offen gesagt
      if (e.sku && !e.skuKnown) message += " – " + this.$t("returns.extract.skuUnknown", { sku: e.sku });
      if (e.skuFromGtin) message += " – " + this.$t("returns.extract.skuFromGtin");

      // Die SKU hebt die Kandidaten-Scores; das Ergebnis kommt direkt mit
      if (res.match) {
        this.candidates = res.match.candidates || [];
        this.diagnosis = res.match.diagnosis || [];
        this.searched = true;
      }
      return { kind: "ean", state: "ok", message };
    },

    // --- Schritt 2: Zuordnung ---
    openScanner(ziel) {
      this.scannerZiel = ziel;
      this.scannerOpen = true;
    },
    onScanned({ value }) {
      this.scannerOpen = false;
      if (this.scannerZiel === "location") {
        this.uebernimmScanOrt(value);
        return;
      }
      this.search.referenceNumber = value;
      // Ein reiner Zahlencode ist eher eine Sendungsnummer als eine Marktplatz-Referenz -
      // beides mitgeben, die Kaskade entscheidet.
      if (/^[0-9\s-]{10,}$/.test(value)) {
        this.search.returnTracking = value;
        this.checkDuplicates(value);
      }
      this.findCandidates();
    },
    async checkDuplicates(trackingNumber) {
      try {
        this.duplicates = await ReturnsService.duplicates(trackingNumber, this.returnId);
        if (this.duplicates.length) this.duplicateAck = false;
      } catch (err) {
        this.duplicates = [];
      }
    },
    openDuplicate(duplicate) {
      this.$router.push("/m/retoure/" + duplicate.id);
    },
    async findCandidates() {
      this.matching = true;
      try {
        // Suchdaten an der Retoure festhalten (Doku/Diagnose). Schlaegt das fehl - etwa
        // weil die Retoure schon gebucht und damit gesperrt ist -, wird trotzdem gesucht.
        await ReturnsService.update(this.returnId, {
          referenceNumber: this.search.referenceNumber && this.search.referenceNumber !== this.prefillReference ? this.search.referenceNumber : null,
          returnTrackingNumber: this.search.returnTracking || null,
          senderName: this.search.senderName || null,
          senderZipcode: this.search.senderZipcode || null,
        }).catch(() => {});
        const res = await ReturnsService.candidates(this.returnId, {
          referenceNumber: this.search.referenceNumber || undefined,
          returnTracking: this.search.returnTracking || undefined,
          senderName: this.search.senderName || undefined,
          senderZipcode: this.search.senderZipcode || undefined,
        });
        this.candidates = res.candidates || [];
        this.diagnosis = res.diagnosis || [];
        this.searched = true;
        // Auch bei manuell eingetippter Sendungsnummer pruefen - nicht nur nach Scan/Extraktion
        if (this.search.returnTracking) this.checkDuplicates(this.search.returnTracking);
      } catch (err) {
        this.notifyError(err, "load_failed");
      } finally {
        this.matching = false;
      }
    },
    candidateKey(c) {
      return c.source + ":" + (c.orderId || c.afterbuyOrderId);
    },
    scoreClass(score) {
      if (score >= 85) return "m-cap__score--exact";
      if (score >= 50) return "m-cap__score--fuzzy";
      return "m-cap__score--weak";
    },
    async assign(candidate) {
      this.saving = true;
      try {
        await ReturnsService.assign(this.returnId, {
          orderId: candidate.orderId || null,
          afterbuyOrderId: candidate.afterbuyOrderId || null,
        });
        this.assignedLabel = candidate.customerOrderId || candidate.customerName || "#" + (candidate.orderId || candidate.afterbuyOrderId);
        this.selectedCandidate = null;
        this.notifyOk("returns.match.assigned");
        // Positionen aus dem Auftrag vorbelegen
        const detail = await ReturnsService.get(this.returnId);
        // Erkannte SKU (EAN-Sticker/Boxbild) schlaegt die Bestellung - Falschversand nicht erneut falsch buchen (P10)
        this.items = this.extractedSku ? [{ sku: this.extractedSku, quantity: 1, condition: "A" }] : (detail.orderSkus || []).map((s) => ({ sku: s.sku, quantity: s.quantity, condition: "A" }));
        this.step = 3;
      } catch (err) {
        this.notifyError(err, "load_failed");
      } finally {
        this.saving = false;
      }
    },
    skipAssign() {
      this.step = 3;
      this.prefillItems();
    },
    // Vorauswahl der Positionen (P10): SKU vom EAN-Sticker, sonst liest der Server das
    // Boxbild (ean-Modus mit box_side_1-Fallback), sonst bleibt es bei der Bestell-Vorbelegung.
    // Laeuft die Erkennung beim Step-Wechsel noch, zeigt der Positionen-Schritt einen Spinner
    // und traegt das Ergebnis danach selbst ein (Kundenfall 25.08.: "packt ihn nicht in die Maske").
    async prefillItems() {
      if (this.items.length) return;
      if (this.extractedSku) {
        this.items = [{ sku: this.extractedSku, quantity: 1, condition: "A" }];
        return;
      }
      this.itemsPrefillPending = true;
      try {
        if (this.extractingKinds.includes("ean")) {
          while (this.extractingKinds.includes("ean")) {
            await new Promise((r) => setTimeout(r, 300));
          }
        } else if (!this.eanCheck) {
          await this.runExtract("ean");
        }
        if (!this.items.length && this.extractedSku) {
          this.items = [{ sku: this.extractedSku, quantity: 1, condition: "A" }];
        }
      } finally {
        this.itemsPrefillPending = false;
      }
    },

    // --- Schritt 3: Positionen ---
    isBware(p) {
      return (p.condition || "").toLowerCase() === "b-ware";
    },
    // Zustandspruefung fuer die A/B-Warnung: bevorzugt den Artikelstamm; ist er (noch)
    // nicht geladen, greift das Namensschema - beide sind laut Stammdaten deckungsgleich.
    isBwareSku(sku) {
      if (!sku) return false;
      const p = this.allSkus.find((x) => x.sku === sku);
      if (p) return this.isBware(p);
      return /_bware$/i.test(sku.trim());
    },
    conditionLabel(c) {
      if (c === "B") return this.$t("returns.items.conditionB");
      if (c === "S") return this.$t("returns.items.conditionS");
      if (c === "U") return this.$t("returns.items.conditionU");
      return this.$t("returns.items.conditionA");
    },
    saveNotes() {
      if (!this.returnId) return;
      ReturnsService.update(this.returnId, { notes: this.notes }).catch(() => {});
    },
    async openSkuPicker(idx) {
      this.skuPickerIdx = idx;
      this.skuSearch = "";
      if (this.allSkus.length) return;
      this.skuLoading = true;
      try {
        this.allSkus = await ShippingDataService.getProductLookup();
      } catch (err) {
        this.notifyError(err, "load_failed");
        this.skuPickerIdx = null;
      }
      this.skuLoading = false;
    },
    chooseSku(p) {
      const item = this.items[this.skuPickerIdx];
      if (item) {
        item.sku = p.sku;
        // Der Artikelstamm weiss, ob es B-Ware ist - dann muss der Zustand nicht geraten werden
        if (this.isBware(p)) item.condition = "B";
      }
      this.skuPickerIdx = null;
    },
    // B-Ware-SKU nach derselben Konvention wie beim Anlegen im Desktop (Products.vue): Basis-SKU + "_bware".
    // Doppeltes Anhaengen vermeiden, falls schon eine B-Ware-SKU gescannt wurde.
    bwareSku(sku) {
      const base = (sku || "").trim();
      if (!base) return "";
      return base.endsWith("_bware") ? base : base + "_bware";
    },
    async saveItems() {
      const valid = this.items.filter((i) => i.sku && i.quantity > 0);
      // Auch eine LEERE Liste wird gespeichert - sonst blieb eine entfernte Position
      // serverseitig einfach stehen (Kundenmeldung #299, P11)
      this.saving = true;
      try {
        await ReturnsService.setItems(
          this.returnId,
          valid.map((i) => ({ sku: i.sku, quantity: i.quantity, condition: i.condition, targetSku: i.condition === "B" ? this.bwareSku(i.sku) : null }))
        );
        if (valid.length) this.notifyOk("returns.items.saved");
      } catch (err) {
        this.notifyError(err, "no_items");
        this.saving = false;
        return;
      }
      this.saving = false;
      await this.loadBooking();
      this.step = 4;
    },
    async loadBooking() {
      try {
        const preview = await ReturnsService.bookingPreview(this.returnId);
        this.bookingLocations = preview.locations || [];
        this.bookingWarning = !!preview.alreadyBookedWarning;
        // Bewusst KEINE Vorbelegung (P10.3): der Platz muss aktiv gewaehlt werden
        // (manuell, Scanner oder Foto) - sonst wird zu leicht auf den falschen gebucht.
        this.selectedLocationId = null;
        // Server-Ids der Positionen fuer die Buchung nachladen
        const detail = await ReturnsService.get(this.returnId);
        this.serverItems = detail.items || [];
      } catch (e) {
        this.bookingLocations = [];
      }
    },

    // --- Schritt 4 ---
    async finish() {
      this.saving = true;
      try {
        await ReturnsService.completeCapture(this.returnId);
        this.notifyOk("returns.summary.finished");
        this.$router.push("/m/retoure");
      } catch (err) {
        this.notifyError(err, "missing_required_photos");
      } finally {
        this.saving = false;
      }
    },
    async book() {
      this.saving = true;
      try {
        const items = (this.serverItems || [])
          .filter((i) => !i.warehouseInventoryId)
          .map((i) => ({ returnItemId: i.id, storageLocationId: this.selectedLocationId }));
        await ReturnsService.book(this.returnId, items);
        this.notifyOk("returns.booking.booked");
        this.$router.push("/m/retoure");
      } catch (err) {
        this.notifyError(err, "invalid_status");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.m-mp {
  display: inline-block;
  min-width: 28px;
  padding: 1px 4px;
  border-radius: 4px;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  text-align: center;
  margin-right: 4px;
}
.m-cap {
  padding: 12px 12px 24px;
}

.m-cap__steps {
  display: flex;
  gap: 4px;
  list-style: none;
  padding: 0;
  margin: 0 0 14px;

  li {
    flex: 1;
    text-align: center;
    font-size: 11px;
    color: #9e9e9e;
    padding: 6px 2px;
    border-bottom: 3px solid #e0e0e0;

    &.is-on {
      color: #212121;
      border-color: #7f915a;
      font-weight: 500;
    }
    &.is-done {
      color: #7f915a;
      border-color: #c6d6a5;
    }
  }
}

.m-cap__hint {
  color: #757575;
  font-size: 14px;
  margin: 0 0 12px;
}

// Sofortpruefung der Fotos
.m-cap__check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
  font-size: 13px;

  p {
    margin: 0;
  }

  i {
    font-size: 20px;
    flex: none;
  }

  &--running {
    background: #eceff1;
    color: #455a64;
  }
  &--ok {
    background: #f1f5e8;
    color: #7f915a;
  }
  &--failed {
    background: #ffebee;
    color: #c62828;
  }
}

.m-cap__check-body {
  flex: 1;
}

.m-cap__retake {
  margin-top: 8px;
  background: #c62828;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

// Dublettenwarnung - flaechig wie die uebrigen Hinweisboxen, kein Rahmen
.m-cap__dup {
  background: #fff3e0;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #6d4c41;

  strong {
    display: block;
    margin-bottom: 4px;
    color: #e65100;
  }
  p {
    margin: 0 0 4px;
  }
}

.m-cap__dup-hint {
  font-size: 12px;
  opacity: 0.8;
}

.m-cap__dup-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;

  button {
    flex: 1;
    border-radius: 8px;
    padding: 9px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
  }
}

.m-cap__dup-open {
  background: #ef6c00;
  color: #fff;
  border: 0;
}

.m-cap__dup-go {
  background: #fff;
  color: #6d4c41;
  border: 1px solid #ffcc80;
}

.m-spin {
  animation: m-cap-rotate 1.2s linear infinite;
}
@keyframes m-cap-rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.m-cap__typebtn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  background: #fff;
  border: 0;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
  padding: 16px;
  margin-bottom: 10px;
  font-size: 16px;
  cursor: pointer;

  i {
    color: #7f915a;
    font-size: 28px;
  }
}

.m-cap__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}

/* Untergeordnete Foto-Gruppen (Artikel/Abschluss) ruecken etwas enger an ihre Ueberschrift */
.m-cap__grid--sub {
  margin-top: 6px;
}

.m-cap__primary {
  display: block;
  width: 100%;
  background: #7f915a;
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 13px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 10px;

  &:disabled {
    background: #bdbdbd;
  }
}

.m-cap__secondary {
  display: block;
  width: 100%;
  background: none;
  color: #757575;
  border: 1px solid #bdbdbd;
  border-radius: 10px;
  padding: 12px;
  font-size: 14px;
  cursor: pointer;
  margin-top: 10px;
}

.m-cap__search {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
  padding: 12px;
  margin-bottom: 12px;

  label {
    display: block;
    font-size: 12px;
    color: #757575;
    margin: 8px 0 4px;
  }

  input {
    width: 100%;
    height: 42px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 0 10px;
    font-size: 15px;
  }
}

.m-cap__searchrow {
  display: flex;
  gap: 8px;

  .m-cap__zip {
    max-width: 110px;
  }
}

.m-cap__extract {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background: #fff;
  border: 1px solid #7f915a;
  color: #7f915a;
  border-radius: 10px;
  padding: 12px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 10px;

  &:disabled {
    color: #9e9e9e;
    border-color: #e0e0e0;
  }
}

.m-cap__scanbtn {
  width: 44px;
  height: 42px;
  flex: none;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  cursor: pointer;

  i {
    color: #7f915a;
  }
}

.m-cap__diag {
  background: #fff8e1;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 12px;
  margin-bottom: 12px;

  strong {
    display: block;
    margin-bottom: 4px;
  }
  ul {
    margin: 0;
    padding-left: 16px;
  }

  // Mit exaktem Treffer ist das Panel Bestaetigung, keine Fehlersuche
  &--hit {
    background: #f1f5e8;
  }
}

// Kartenstile wie in ReturnList.vue / ShippingPlanList.vue (dort ebenfalls scoped definiert)
.m-cards {
  list-style: none;
  margin: 0;
  padding: 0;
}

.m-card {
  display: block;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
  padding: 12px;
  margin-bottom: 10px;
  color: #212121;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  &__title {
    font-size: 15px;
    font-weight: 500;
  }
  &__sub {
    color: #757575;
    font-size: 13px;
    margin: 4px 0;
  }
}

.m-cap__cand {
  width: 100%;
  text-align: left;
  border: 0;
  cursor: pointer;
  font: inherit; // Buttons erben die Seitenschrift sonst nicht
}

.m-cap__rekla {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: #7f915a;
  font-size: 13px;
  font-weight: 600;
  padding: 2px 6px;

  .material-icons {
    font-size: 18px;
  }
}

.m-cap__score {
  flex: none;
  font-size: 13px;
  font-weight: 700;
  border-radius: 12px;
  padding: 2px 10px;
  color: #fff;

  &--exact {
    background: #7f915a;
  }
  &--fuzzy {
    background: #ff9800;
  }
  &--weak {
    background: #9e9e9e;
  }
}

.m-cap__fields {
  margin: 4px 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.m-cap__field {
  font-size: 11px;
  border-radius: 8px;
  padding: 1px 7px;

  &--ok {
    background: #f1f5e8;
    color: #7f915a;
  }
  &--bad {
    background: #ffebee;
    color: #c62828;
  }
}

.m-cap__memo {
  font-size: 12px;
  color: #6d4c41;
  background: #fff3e0;
  border-radius: 8px;
  padding: 6px 8px;
  margin: 6px 0 0;
  white-space: pre-line;
}

// Chip-Stile wie in ReturnList.vue / ShippingPlanList.vue (dort ebenfalls scoped definiert)
.m-chips {
  display: flex;
  gap: 6px;

  // Auf A-Ware ist die Chip-Reihe das letzte Element der Position, auf B-Ware folgt das SKU-Feld
  &:not(:last-child) {
    margin-bottom: 8px;
  }
}

.m-chip {
  flex: none;
  border: 1px solid #bdbdbd;
  background: #fff;
  color: #616161;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;

  &--on {
    background: #7f915a;
    border-color: #7f915a;
    color: #fff;
  }
}

.m-cap__item {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
  padding: 10px 12px;
  margin-bottom: 10px;
}

.m-cap__itemhead {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.m-cap__sku {
  flex: 1;
  width: 100%;
  height: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 15px;
}

.m-cap__bsku {
  margin: 0;
  font-size: 13px;
  color: #616161;
}

// Sieht aus wie das Eingabefeld daneben, ist aber ein Button - Tippen oeffnet die Auswahl
.m-cap__skubtn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  background: #fff;
  text-align: left;
  color: #212121;
  font: inherit;
  cursor: pointer;

  &.is-empty {
    color: #9e9e9e;
  }

  i {
    color: #757575;
  }
}

.m-cap__skulist {
  max-height: 46vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.m-cap__bwaretag {
  flex: none;
  font-size: 11px;
  border-radius: 8px;
  padding: 1px 7px;
  background: #fff3e0;
  color: #ef6c00;
}

.m-cap__qty {
  display: flex;
  align-items: center;
  gap: 2px;

  button {
    width: 34px;
    height: 34px;
    border: 1px solid #e0e0e0;
    background: #fafafa;
    border-radius: 8px;
    font-size: 18px;
    cursor: pointer;
  }
  span {
    min-width: 26px;
    text-align: center;
    font-weight: 500;
  }
}

.m-cap__remove {
  border: 0;
  background: none;
  color: #bdbdbd;
  cursor: pointer;
}

.m-cap__summary {
  list-style: none;
  padding: 0;
  margin: 0 0 14px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
    padding: 12px;
    margin-bottom: 8px;
    font-size: 14px;

    i.ok {
      color: #7f915a;
    }
    i.bad {
      color: #f44336;
    }
    i.warn {
      color: #ff9800;
    }
  }
}

.m-cap__booking {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
  padding: 12px;
  margin-bottom: 10px;

  label {
    display: block;
    font-size: 12px;
    color: #757575;
    margin-bottom: 4px;
  }
}

.m-cap__scanloc {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background: #fff;
  border: 1px solid #7f915a;
  color: #7f915a;
  border-radius: 10px;
  padding: 11px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 8px;

  &:disabled {
    color: #9e9e9e;
    border-color: #e0e0e0;
  }
}

.m-cap__hiddenfile {
  display: none;
}

.m-cap__loccheck {
  font-size: 13px;
  margin: 8px 0 0;
  color: #7f915a;

  &.is-failed {
    color: #c62828;
  }
  &.is-running {
    color: #757575;
  }
}

.m-cap__notes {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
  padding: 12px;
  margin-bottom: 10px;

  label {
    display: block;
    font-size: 12px;
    color: #757575;
    margin-bottom: 4px;
  }

  textarea {
    width: 100%;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 14px;
    font-family: inherit;
    resize: vertical;
    box-sizing: border-box;
  }
}

.m-cap__select {
  width: 100%;
  height: 42px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  font-size: 15px;
  padding: 0 8px;
}

.m-cap__warn {
  background: #ffebee;
  color: #c62828;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  margin: 0 0 10px;
}

.m-hint {
  text-align: center;
  color: #9e9e9e;
  padding: 14px 0;

  &--small {
    font-size: 12px;
  }

  &.m-cap__prefill {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
}
</style>
